import React, { useEffect, useMemo, useRef, useState } from "react";
import { Share2, Link2, QrCode, IdCard, X } from "lucide-react";
import Gnavbar from "../components/Gnavbar";
import BottomNavbar from "../components/BottomNavbar";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { getBureauWebsiteUrl, hasActiveMembership } from "../utils/bureau";
import html2canvas from "html2canvas";
import { QRCodeCanvas } from "qrcode.react";
import DynamicVisitingCard, {
  VISITING_CARD_THEMES,
  VisitingCardTheme,
} from "../components/VisitingCards/DynamicVisitingCard";
import { shareViaNative } from "../helpers/nativeShare";
import { shareBlobAsFile } from "../helpers/shareFile";

const ShareBureau = () => {
  const bureauInfo = useSelector((state: RootState) => state.bureau.data);
  const navigate = useNavigate();
  const isPaidAccount = hasActiveMembership(bureauInfo?.paymentStatus);

  // useEffect(() => {
  //   if (bureauInfo && !isPaidAccount) {
  //     navigate("/membership/plans", { replace: true });
  //   }
  // }, [bureauInfo, isPaidAccount, navigate]);
  // if (bureauInfo && !isPaidAccount)

  const bureauWebsite = getBureauWebsiteUrl(bureauInfo);

  const qrCardRef = useRef<HTMLDivElement | null>(null);

  const [showModal, setShowModal] = useState(false);
  const [selectedCard, setSelectedCard] = useState<string>(
    VISITING_CARD_THEMES[0]?.id ?? "",
  );
  const selectedTheme = useMemo<VisitingCardTheme | undefined>(
    () => VISITING_CARD_THEMES.find((theme) => theme.id === selectedCard),
    [selectedCard],
  );

  // ✅ Copy link to clipboard
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(bureauWebsite);
      toast.success("🔗 Website link copied!");
    } catch {
      toast.error("❌ Failed to copy link.");
    }
  };

  const downloadImageFile = async (url: string, filename: string) => {
    try {
      const response = await fetch(url, { mode: "cors" });
      const blob = await response.blob();

      const link = document.createElement("a");
      const objectUrl = URL.createObjectURL(blob);
      link.href = objectUrl;
      link.download = filename;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(objectUrl);
    } catch {
      toast.error("Unable to download card");
    }
  };

  const generateQrCardImage = async (): Promise<Blob> => {
    if (!qrCardRef.current) {
      throw new Error("QR card not ready");
    }

    const canvas = await html2canvas(qrCardRef.current, {
      backgroundColor: "#ffffff",
      scale: 2,
    });

    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error("Failed to create QR image"));
        }
      }, "image/png");
    });
  };

  // ✅ Share bureau website link
  const handleShareWebsite = async () => {
    if (!bureauWebsite) {
      toast.error("Website details are not available yet.");
      return;
    }
    const aboutText = bureauInfo?.about?.trim() || "";

    const shareText = aboutText
      ? `${aboutText}\n\nWebsite: ${bureauWebsite}`
      : `Website: ${bureauWebsite}`;

    try {
      await shareViaNative({
        title: bureauInfo?.bureauName ?? "Marriage Bureau",
        text: shareText,
      });
    } catch (error) {
      console.error("Website share failed:", error);

      try {
        await navigator.clipboard.writeText(shareText);
        toast.info("Website details copied.");
      } catch {
        toast.error("Unable to share website.");
      }
    }
  };

  // ✅ Share or download selected visiting card
  const handleShareVisitingCard = async () => {
    if (!selectedTheme) {
      toast.error("Please choose a visiting card design first.");
      return;
    }
    if (!cardRef.current) {
      toast.error("Visiting card preview not ready.");
      return;
    }

    await new Promise((r) => setTimeout(r, 80));

    try {
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: "#ffffff",
        scale: 3,
        useCORS: true,
      });

      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((fileBlob) => {
          if (fileBlob) resolve(fileBlob);
          else reject(new Error("Unable to create card blob"));
        }, "image/png");
      });

      await shareBlobAsFile(
        blob,
        `visiting-card-${selectedTheme.id}.png`,
        bureauInfo?.bureauName ?? "MeeMarriage Bureau",
        "Share our visiting card and connect with us!",
      );
      toast.success("Visiting card shared / downloaded.");
    } catch (err) {
      console.error(err);
      toast.error("Failed to share visiting card.");
    }
  };

  const handleShareQRCode = async () => {
    try {
      const qrBlob = await generateQrCardImage();
      await shareBlobAsFile(
        qrBlob,
        "bureau-qr-code.png",
        "MeeMarriage QR",
        "Scan to view my MeeMarriage bureau website.",
      );
      toast.success("QR code shared / downloaded.");
    } catch (error) {
      console.error(error);
      toast.error("Unable to prepare QR card.");
    }
  };

  const cardRef = useRef<HTMLDivElement | null>(null);

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-purple-50 via-white to-blue-50">
      <Gnavbar />

      <main className="flex-1 px-4 pb-[calc(var(--bottom-nav-height)+var(--bottom-safe-area)+2rem)] pt-10 sm:px-8 sm:pb-10">
        {/* Header */}
        <div className="flex flex-col items-center text-center mb-10">
          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 flex items-center justify-center text-white mb-4 shadow-lg">
            <Share2 size={30} className="sm:w-8 sm:h-8" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">
            Share Your Bureau
          </h1>
          <p className="text-gray-500 text-sm sm:text-base mt-2 max-w-md">
            Promote your business using the options below
          </p>
        </div>

        {/* Layout Sections */}
        <div className="flex flex-col gap-6">
          {/* 🌐 Website Link */}
          <section className="p-5 sm:p-8 border sm:rounded-xl sm:border-gray-200 bg-gradient-to-r from-purple-50 to-blue-50 sm:hover:shadow-md transition">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-r from-purple-600 to-blue-500 flex items-center justify-center text-white shadow">
                <Link2 size={20} />
              </div>
              <h2 className="text-lg sm:text-xl font-semibold text-gray-800">
                Share Website
              </h2>
            </div>
            <p className="text-sm text-gray-600 mb-3 break-all">
              {bureauWebsite}
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleCopyLink}
                className="flex-1 bg-gradient-to-r from-purple-600 to-blue-500 text-white py-2 rounded-lg font-medium shadow hover:opacity-90 transition"
              >
                Copy Link
              </button>
              <button
                onClick={handleShareWebsite}
                className="flex-1 border-2 border-purple-500 text-purple-600 py-2 rounded-lg font-medium hover:bg-purple-50 transition"
              >
                Share
              </button>
            </div>
          </section>

          {/* 🧾 Visiting Card */}
          <section className="p-5 sm:p-8 border sm:rounded-xl sm:border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50 sm:hover:shadow-md transition">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-r from-blue-600 to-indigo-500 flex items-center justify-center text-white shadow">
                <IdCard size={20} />
              </div>
              <h2 className="text-lg sm:text-xl font-semibold text-gray-800">
                Share Visiting Card
              </h2>
            </div>
            <p className="text-sm text-gray-600 mb-3">
              Choose one of the designs and share or download.
            </p>
            <button
              className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white py-2 rounded-lg font-medium shadow hover:opacity-90 transition"
              onClick={() => setShowModal(true)}
            >
              Choose & Share Visiting Card
            </button>
          </section>

          {/* 🔲 QR Code */}
          <section className="p-5 sm:p-8 border sm:rounded-xl sm:border-gray-200 bg-gradient-to-r from-purple-50 to-pink-50 sm:hover:shadow-md transition">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-r from-pink-600 to-purple-600 flex items-center justify-center text-white shadow">
                <QrCode size={20} />
              </div>
              <h2 className="text-lg sm:text-xl font-semibold text-gray-800">
                Share QR Code
              </h2>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Let people scan and visit your website instantly.
            </p>
            <div className="flex flex-col items-center gap-4">
              <div
                ref={qrCardRef}
                className="bg-white rounded-lg border shadow-sm px-6 py-4 flex flex-col items-center gap-3"
              >
                <p className="text-base font-semibold text-gray-800">
                  Scan our website
                </p>
                <QRCodeCanvas
                  value={bureauWebsite}
                  size={180}
                  bgColor="#ffffff"
                  fgColor="#111827"
                  includeMargin
                />
              </div>
              <button
                onClick={handleShareQRCode}
                className="mb-2 bg-gradient-to-r from-purple-600 to-pink-500 text-white px-5 py-2 rounded-lg font-medium hover:opacity-90 transition"
              >
                Share / Download QR
              </button>
            </div>
          </section>
        </div>
      </main>

      {/* 🧾 Modal for Visiting Cards */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-800">
                Choose a Visiting Card Design
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={20} />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto pr-2">
              {VISITING_CARD_THEMES.map((theme) => (
                <div
                  key={theme.id}
                  className={`border rounded-xl cursor-pointer overflow-hidden transition transform hover:-translate-y-0.5 ${
                    selectedCard === theme.id
                      ? "ring-2 ring-purple-500 shadow-lg"
                      : "shadow-sm"
                  }`}
                  onClick={() => setSelectedCard(theme.id)}
                >
                  <DynamicVisitingCard theme={theme} bureauInfo={bureauInfo} />
                  <p className="text-sm font-medium text-center py-2 text-gray-600">
                    {theme.name}
                  </p>
                </div>
              ))}
            </div>

            {selectedTheme && (
              <div className="sticky bottom-0 bg-white pt-4 pb-[calc(env(safe-area-inset-bottom)+1.5rem)] flex justify-center border-t">
                <button
                  onClick={handleShareVisitingCard}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg font-medium shadow hover:opacity-90 transition"
                >
                  Share / Download Selected Card
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <div
        style={{
          position: "fixed",
          top: "-10000px",
          left: "-10000px",
          pointerEvents: "none",
          opacity: 0,
        }}
      >
        {selectedTheme && (
          <DynamicVisitingCard
            ref={cardRef}
            bureauInfo={bureauInfo}
            theme={selectedTheme}
          />
        )}
      </div>

      <BottomNavbar />
    </div>
  );
};

export default ShareBureau;
